import { createFileRoute, isRedirect, redirect } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { LayoutAuth } from "#/layouts/layout.auth";
import { acceptInviteNewFn } from "#/modules/generic.invite/invite.functions";
import { useAppForm } from "#/modules/util.forms/form";
import { passwordSchema } from "#/modules/util.validation/schema.password";
import { op, syncOpenPanelUser } from "#/modules/util.instrumentation/op";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { z } from "zod/v4";
import MaterialSymbolsCheckSmall from "~icons/material-symbols/check-small";
import MaterialSymbolsCheckCircleOutline from "~icons/material-symbols/check-circle-outline";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { InviteType } from "#/schema.nswag";

const newInviteSchema = z.object({
  firstName: z.string().min(1, { message: m["validation.first_name_required"]() }),
  lastName: z.string().min(1, { message: m["validation.last_name_required"]() }),
  password: passwordSchema,
});

export const Route = createFileRoute("/$locale/{-$schoolyear}/invite/new")({
  validateSearch: z.object({
    inviteId: z.string().catch(""),
    token: z.string().catch(""),
    schoolName: z.string().catch(""),
    inviteeEmail: z.string().catch(""),
    inviteType: apiEnum(InviteType).catch("Docent"),
  }),
  beforeLoad: ({ search, params }) => {
    if (!search.inviteId || !search.token || !search.schoolName || !search.inviteeEmail) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/login",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
  },
  component: InviteNewPage,
});

const uspItems = [
  m["invite.new.usp.groei"](),
  m["invite.new.usp.rapporten"](),
  m["invite.new.usp.administratie"](),
  m["invite.new.usp.gebruiksvriendelijk"](),
];

function InviteNewPage() {
  const { inviteId, token, schoolName, inviteeEmail, inviteType } = Route.useSearch();
  const acceptInviteNew = useServerFn(acceptInviteNewFn);

  const form = useAppForm({
    defaultValues: { firstName: "", lastName: "", password: "" },
    validators: { onSubmit: newInviteSchema },
    onSubmit: async ({ value }) => {
      // Identify before tracking so both events are profile-attributed, and fire
      // registration_completed alongside invite_accepted: an account is created here too,
      // and registration_completed is the single conversion event across both signup flows
      // (self-serve vs invite — distinguished by the `source` property).
      const trackAccountCreated = () => {
        syncOpenPanelUser(
          { email: inviteeEmail, firstName: value.firstName, lastName: value.lastName },
          null,
        );
        void op.track("invite_accepted", { existing_account: false, invite_type: inviteType });
        void op.track("registration_completed", { source: "invite", invite_type: inviteType });
      };
      try {
        const res = await acceptInviteNew({
          data: {
            inviteId,
            token,
            inviteeEmail,
            inviteType,
            firstName: value.firstName,
            lastName: value.lastName,
            password: value.password,
          },
        });
        if (res) {
          form.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
          return;
        }
        // undefined normally means loginCallback threw a redirect (caught above)
        trackAccountCreated();
      } catch (error) {
        if (isRedirect(error)) {
          trackAccountCreated();
          throw error;
        }
        captureUnexpectedError(error, "invite-new");
        form.setErrorMap({ onSubmit: { form: m["common.something_went_wrong"](), fields: {} } });
      }
    },
  });

  return (
    <LayoutAuth showVisuals={false} containerClassName="max-w-lg w-full mb-[5%]">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{m["invite.new.title"]({ schoolName })}</CardTitle>
          <CardDescription>{m["invite.new.description"]()}</CardDescription>
        </CardHeader>
        <CardContent>
          <form.AppForm>
            <form.FormRoot className="flex flex-col gap-4">
              <ul className="space-y-2">
                {uspItems.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center
                      justify-center rounded-full bg-green-100 dark:bg-green-900/30"
                    >
                      <MaterialSymbolsCheckSmall className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </span>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Read-only email display */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">{m["common.fields.email"]()}</label>
                <input
                  type="email"
                  value={inviteeEmail}
                  disabled
                  autoComplete="email"
                  className="border-input bg-muted rounded-md border px-3 py-2 text-sm opacity-70"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <form.AppField
                  name="firstName"
                  children={(field) => <field.TextField label={m["common.fields.first_name"]()} />}
                />
                <form.AppField
                  name="lastName"
                  children={(field) => <field.TextField label={m["common.fields.last_name"]()} />}
                />
              </div>

              <form.AppField
                name="password"
                children={(field) => (
                  <field.PasswordTextField label={m["common.fields.password"]()} showRequirements />
                )}
              />

              <form.SubmitButton className="mt-2 w-full">
                <MaterialSymbolsCheckCircleOutline className="mr-2 h-5 w-5" />
                {m["invite.new.submit"]()}
              </form.SubmitButton>
            </form.FormRoot>
          </form.AppForm>
        </CardContent>
      </Card>

      <div className="text-muted-foreground mt-6 text-center text-sm [&_a]:underline">
        <ParaglideMessage
          message={m["invite.new.terms"]}
          markup={{
            link: ({ children }) => (
              <a href="https://beweegbaas.nl/privacy" target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
          }}
        />
      </div>
    </LayoutAuth>
  );
}
